/**
 * Profile Save Handler
 * Handles saving profile data to Firestore
 */

import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../services/firebase/firebase';

/**
 * Profile Save Handler
 * Service for saving profile data
 */
const ProfileSaveHandler = {
  /**
   * Save profile data to Firestore
   * 
   * @param {string} userId - User ID
   * @param {Object} profileData - Profile data to save
   * @returns {Promise<void>} Promise that resolves when the profile is saved
   */
  saveProfile: async (userId, profileData) => {
    if (!userId) throw new Error('User ID is required');
    if (!profileData) throw new Error('Profile data is required');
    
    try {
      // Prepare data for update (strip out undefined values)
      const cleanedData = Object.fromEntries(
        Object.entries(profileData).filter(([_, value]) => value !== undefined)
      );
      
      // Add last updated timestamp
      const dataToUpdate = {
        ...cleanedData,
        lastUpdated: new Date()
      };
      
      // Update profile document in Firestore
      const profileRef = doc(db, 'profiles', userId);
      await updateDoc(profileRef, dataToUpdate);
      
      return true;
    } catch (error) {
      console.error('Error saving profile:', error);
      throw error;
    }
  },
  
  /**
   * Validate profile data before saving
   * 
   * @param {Object} profileData - Profile data to validate
   * @returns {Object} Validation result with isValid flag and errors object
   */
  validateProfile: (profileData) => {
    const errors = {};
    
    // Basic Info validation
    if (!profileData.basicInfo?.name) {
      errors.name = 'Name is required';
    }
    
    if (!profileData.basicInfo?.username) {
      errors.username = 'Username is required';
    } else if (!/^[a-zA-Z0-9_]+$/.test(profileData.basicInfo.username)) {
      errors.username = 'Username can only contain letters, numbers, and underscores';
    }
    
    if (!profileData.basicInfo?.email) {
      errors.email = 'Email is required';
    }
    
    // Academic Info validation
    if (!profileData.academic?.university) {
      errors.university = 'University is required';
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }
};

export default ProfileSaveHandler;